class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.defaultSearch = this.querySelector(".default--search");
    this.demoSearch = this.querySelector(".wbsclose");

    this.selectedCategory = '';
    this.setupCategoryListeners();
    this.handleViewportResize = () => {
      if (this.isOpen) {
        this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
      }
    };
    (window.visualViewport || window).addEventListener('resize', this.handleViewportResize);

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onKkmSearchCartClick.bind(this));
    this.querySelector(".wbsclose")?.addEventListener('click', this.onClick.bind(this));

    this.kkmCartState = null;
    this.kkmCartRequest = null;
    this.kkmCartLastFetched = 0;
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.kkmCartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.cartData?.items) {
          this.kkmCartState = event.cartData;
          this.kkmCartLastFetched = Date.now();
          this.applyKkmCartState(event.cartData);
        } else {
          this.syncKkmSearchCartFromServer(true);
        }
      });
    }
  }

  setupCategoryListeners() {
    const select = document.getElementById('SearchFilter');
    if (!select) return;

    this.selectedCategory = select.value?.toLowerCase() || '';

    select.addEventListener('change', (e) => {
      this.setSelectedCategory(e.target.value.toLowerCase());
    });
  }

  setSelectedCategory(category) {
    this.selectedCategory = category;

    const hiddenInput = document.querySelector('#predictive-search-category');
    if (hiddenInput) hiddenInput.value = category;

    if (this.getQuery().length) {
      this.getSearchResults(this.getQuery());
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.defaultSearch.classList.contains('hidden')) {
      this.defaultSearch.classList.add("hidden");
    }

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.defaultSearch.classList.remove("hidden");
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (event.submitter?.closest('quick-add-bulk, product-form')) {
      event.preventDefault();
      return;
    }
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    this.defaultSearch.classList.remove("hidden");
    this.demoSearch.classList.remove("hidden");

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.defaultSearch.classList.add("hidden");
        this.demoSearch.classList.add("hidden");
        this.close();
      }
    }, 100);
  }

  onClick() {
    setTimeout(() => {
      this.defaultSearch.classList.add("hidden");
      this.demoSearch.classList.add("hidden");
      this.close();
    });
  }

  onKeyup(event) {
    if (event.target !== this.input) return;
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'Escape':
        this.close();
        this.defaultSearch?.classList.add('hidden');
        this.demoSearch?.classList.add('hidden');
        this.input.blur();
        break;
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.target !== this.input) return;
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;

    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'))?.length > 1) return;
      searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.querySelectorAll('li, button.predictive-search__item')
    ).filter(el => el.offsetParent !== null);

    let activeIndex = 0;
    if (moveUp && !selectedElement) return;

    let selectedIndex = -1;
    allVisibleElements.forEach((el, i) => {
      if (el === selectedElement) selectedIndex = i;
    });

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeIndex = selectedIndex === allVisibleElements.length - 1 ? 0 : selectedIndex + 1;
    } else if (moveUp) {
      activeIndex = selectedIndex === 0 ? allVisibleElements.length - 1 : selectedIndex - 1;
    }

    if (activeIndex === selectedIndex) return;

    const activeElement = allVisibleElements[activeIndex];
    if (!activeElement) return;
    activeElement.setAttribute('aria-selected', true);
    selectedElement?.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    selectedOption?.click();
  }

  getSearchResults(searchTerm) {
    const normalizedCategory = this.selectedCategory?.trim().toLowerCase();
    const queryKey = (searchTerm + (normalizedCategory || ''))
      .replace(/\s+/g, '-')
      .toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const query =
      normalizedCategory && normalizedCategory !== 'all categories'
        ? `${searchTerm} ${normalizedCategory}`
        : searchTerm;

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product,collection,query&resources[limit]=8&resources[limit_scope]=each&resources[options][unavailable_products]=last&section_id=predictive-search`, {
      signal: this.abortController.signal
    })
      .then(response => {
        if (!response.ok) {
          this.close();
          throw new Error(response.status);
        }
        return response.text();
      })
      .then(text => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        this.allPredictiveSearchInstances.forEach(instance => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });

        if (this.getQuery() !== searchTerm) return;
        this.renderSearchResults(resultsMarkup);
      })
      .catch(error => {
        if (error?.code === 20) return;
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(text) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = text;
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
    if (window.KKMCartQuantityCoordinator) {
      window.KKMCartQuantityCoordinator.syncRoot(this);
    } else {
      this.syncKkmSearchCartFromServer();
    }
  }

  syncKkmSearchCartFromServer(force = false) {
    if (!this.querySelector('[data-kkm-search-row]')) return Promise.resolve();

    const coordinator = window.KKMCartQuantityCoordinator;
    if (coordinator) {
      if (!force && coordinator.cart?.items) {
        this.kkmCartState = coordinator.cart;
        this.kkmCartLastFetched = Date.now();
        this.applyKkmCartState(coordinator.cart);
        return Promise.resolve(coordinator.cart);
      }

      return coordinator.refresh()
        .then((cart) => {
          this.kkmCartState = cart;
          this.kkmCartLastFetched = Date.now();
          this.applyKkmCartState(cart);
          return cart;
        })
        .catch((error) => {
          console.error('Predictive search cart sync failed', error);
        });
    }

    const now = Date.now();
    if (!force && this.kkmCartState && now - this.kkmCartLastFetched < 2000) {
      this.applyKkmCartState(this.kkmCartState);
      return Promise.resolve(this.kkmCartState);
    }

    if (this.kkmCartRequest) return this.kkmCartRequest;

    this.kkmCartRequest = fetch(`${routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then((cart) => {
        this.kkmCartState = cart;
        this.kkmCartLastFetched = Date.now();
        this.applyKkmCartState(cart);
        return cart;
      })
      .catch((error) => {
        console.error('Predictive search cart sync failed', error);
      })
      .finally(() => {
        this.kkmCartRequest = null;
      });

    return this.kkmCartRequest;
  }

  applyKkmCartState(cart) {
    if (!cart?.items) return;

    if (window.KKMCartQuantityCoordinator) {
      window.KKMCartQuantityCoordinator.apply(cart, this);
      return;
    }

    const quantities = cart.items.reduce((map, item) => {
      const key = String(item.variant_id);
      map[key] = (map[key] || 0) + item.quantity;
      return map;
    }, {});

    this.querySelectorAll('[data-kkm-search-row]').forEach((row) => {
      const variantId = String(row.dataset.variantId || '');
      if (!variantId) return;

      const quantity = quantities[variantId] || 0;
      const addWrap = row.querySelector('[data-kkm-add-wrap]');
      const qtyWrap = row.querySelector('[data-kkm-qty]');
      const qtyValue = row.querySelector('[data-kkm-qty-value]');
      const hadFocus = row.contains(document.activeElement);

      if (!addWrap || !qtyWrap || !qtyValue) return;

      qtyValue.textContent = quantity;
      addWrap.hidden = quantity > 0;
      qtyWrap.hidden = quantity === 0;

      if (hadFocus) {
        const nextFocus = quantity > 0
          ? row.querySelector('[data-kkm-qty-action="plus"]')
          : row.querySelector('[data-kkm-add]');
        requestAnimationFrame(() => nextFocus?.focus({ preventScroll: true }));
      }
    });
  }

  onKkmSearchCartClick(event) {
    const addButton = event.target.closest('[data-kkm-add]');
    if (addButton) {
      event.preventDefault();
      event.stopPropagation();

      const row = addButton.closest('[data-kkm-search-row]');
      if (!row || row.classList.contains('is-updating')) return;
      this.addKkmSearchVariant(row, row.dataset.variantId);
      return;
    }

    const button = event.target.closest('[data-kkm-qty-action]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    const row = button.closest('[data-kkm-search-row]');
    if (!row || row.classList.contains('is-updating')) return;

    const variantId = row.dataset.variantId;
    const value = row.querySelector('[data-kkm-qty-value]');
    const currentQuantity = parseInt(value?.textContent || '0', 10) || 0;
    const nextQuantity = button.dataset.kkmQtyAction === 'plus'
      ? currentQuantity + 1
      : Math.max(0, currentQuantity - 1);

    this.updateKkmSearchQuantity(row, variantId, nextQuantity);
  }

  addKkmSearchVariant(row, variantId) {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    if (cart?.getSectionsToRender) {
      formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement?.(document.activeElement);
    }

    config.body = formData;
    row.classList.add('is-updating');
    row.setAttribute('aria-busy', 'true');

    fetch(routes.cart_add_url, config)
      .then((response) => response.json())
      .then((state) => {
        if (state.status) throw new Error(state.description || state.message || 'Unable to add item');

        if (cart && state.sections) {
          const cartType = document.body.dataset.cartType;
          cart.renderContents(state, cartType !== 'sticky_cart_bar');

        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'predictive-search',
            cartData: state,
            variantId,
          });
        }

        this.syncKkmSearchCartFromServer(true);
      })
      .catch((error) => {
        console.error('Predictive search add failed', error);
        this.syncKkmSearchCartFromServer(true);
      })
      .finally(() => {
        row.classList.remove('is-updating');
        row.removeAttribute('aria-busy');
        if (this.getAttribute('results') === 'true') this.open();
      });
  }

  updateKkmSearchQuantity(row, variantId, quantity) {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const sections = cart?.getSectionsToRender ? cart.getSectionsToRender().map((section) => section.id) : [];

    row.classList.add('is-updating');
    row.setAttribute('aria-busy', 'true');

    const body = {
      updates: { [variantId]: quantity },
      sections,
      sections_url: window.location.pathname,
    };

    fetch(routes.cart_update_url, {
      ...fetchConfig('javascript'),
      body: JSON.stringify(body),
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then((state) => {
        if (state.errors) throw new Error(state.description || state.errors);

        this.kkmCartState = state;
        this.kkmCartLastFetched = Date.now();
        this.applyKkmCartState(state);

        if (cart && state.sections) {
          const cartType = document.body.dataset.cartType;
          cart.renderContents(state, cartType !== 'sticky_cart_bar');

        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'predictive-search',
            cartData: state,
            variantId,
          });
        }
      })
      .catch((error) => {
        console.error('Predictive search quantity update failed', error);
        this.syncKkmSearchCartFromServer(true);
      })
      .finally(() => {
        row.classList.remove('is-updating');
        row.removeAttribute('aria-busy');
        if (this.getAttribute('results') === 'true') this.open();
      });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(
      this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || ''
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      Math.max(0, Math.floor(((window.visualViewport?.offsetTop || 0) + (window.visualViewport?.height || window.innerHeight)) - this.predictiveSearchResults.parentElement.getBoundingClientRect().top - 15));
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    selected?.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
